import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getServiceTasks } from "../../pages/services/serviceTaskService";
import { SERVICE_STATUS, STATUS_UI } from "../../constants/enums/serviceEnums";

export default function ServiceDashboard() {
  const navigate  = useNavigate();
  const user      = JSON.parse(localStorage.getItem("user") || "{}");
  const companyId = user.company_id || "";

  const [services, setServices] = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [filter,   setFilter]   = useState("all");

  useEffect(() => { loadServices(); }, []);

  const loadServices = async () => {
    try {
      setLoading(true);
      const data = await getServiceTasks({ company_id: companyId });
      setServices(data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // COUNTS
  const counts = {
  all:     services.length,
  open:    services.filter(s => s.status === SERVICE_STATUS.OPEN).length,
  assigned: services.filter(s => s.status === SERVICE_STATUS.ASSIGNED).length,
  in_progress: services.filter(s => s.status === SERVICE_STATUS.IN_PROGRESS).length,
  partial: services.filter(s => s.status === SERVICE_STATUS.PARTIAL).length,
  pending_approval: services.filter(s => s.status === SERVICE_STATUS.COMPLETED_PENDING_APPROVAL).length,
  closed:  services.filter(s =>
    s.status === SERVICE_STATUS.CLOSED || s.status === "completed"  // legacy compat
  ).length,
};

  const statCards = [
    { key: "all",              label: "Total",           count: counts.all,              bg: "#f1f5f9", color: "#334155" },
    { key: SERVICE_STATUS.OPEN,       label: "Open",     count: counts.open,             bg: "#f3f4f6", color: "#374151" },
    { key: SERVICE_STATUS.ASSIGNED,   label: "Assigned", count: counts.assigned,         bg: "#dbeafe", color: "#1e40af" },
    { key: SERVICE_STATUS.IN_PROGRESS,label: "In Progress", count: counts.in_progress,  bg: "#fef3c7", color: "#92400e" },
    { key: SERVICE_STATUS.PARTIAL,    label: "Partial",  count: counts.partial,          bg: "#fed7aa", color: "#9a3412" },
    { key: SERVICE_STATUS.COMPLETED_PENDING_APPROVAL, label: "Pending Approval", count: counts.pending_approval, bg: "#ede9fe", color: "#5b21b6" },
    { key: SERVICE_STATUS.CLOSED,     label: "Closed",   count: counts.closed,           bg: "#d1fae5", color: "#065f46" },
  ];

  const filtered = filter === "all"
    ? services
    : services.filter(s => s.status === filter);

  // ── HELPER — working duration ────────────────────────────────────────────
  const getWorkingDuration = (startedAt) => {
    if (!startedAt?.seconds) return null;
    const startMs = startedAt.seconds * 1000;
    const nowMs   = Date.now();
    const diffMin = Math.floor((nowMs - startMs) / 60000);
    if (diffMin < 1)   return "Just started";
    if (diffMin < 60)  return `${diffMin}m`;
    const hrs = Math.floor(diffMin / 60);
    const min = diffMin % 60;
    return min > 0 ? `${hrs}h ${min}m` : `${hrs}h`;
  };

  // ── HELPER — job type label ──────────────────────────────────────────────
  const JOB_TYPE_LABELS = {
    project:    "Project",
    amc:        "AMC",
    warranty:   "Warranty",
    chargeable: "Chargeable",
    internal:   "Internal",
    other:      "Other",
  };

  return (
    <div className="p-4 pb-20 bg-gray-50 min-h-screen">

      {/* HEADER */}
      <div className="flex items-center justify-between mb-5">
        <h1 className="text-xl font-bold text-slate-800">Service Dashboard</h1>
        <div className="flex gap-2">
  <button onClick={() => navigate("/services", { state: { open_create: true } })}
    className="text-xs bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 rounded-lg font-semibold transition">
    + New Service
  </button>
  <button onClick={loadServices}
    className="text-xs text-slate-400 hover:text-slate-700 border border-slate-200 px-3 py-1.5 rounded-lg">
    ↻ Refresh
  </button>
</div>
      </div>

      {/* STAT CARDS */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        {statCards.map(card => (
          <div key={card.key} onClick={() => setFilter(card.key)}
            className="rounded-2xl p-4 cursor-pointer transition hover:scale-[1.02] shadow-sm border border-white"
            style={{ background: card.bg }}>
            <p className="text-xs font-semibold uppercase tracking-wide" style={{ color: card.color }}>
              {card.label}
            </p>
            <p className="text-3xl font-black mt-1" style={{ color: card.color }}>
              {card.count}
            </p>
            {filter === card.key && (
              <div className="w-6 h-1 rounded-full mt-2" style={{ background: card.color }} />
            )}
          </div>
        ))}
      </div>

      {/* LIST */}
      {loading ? (
        <p className="text-slate-400 text-sm text-center mt-10">Loading...</p>
      ) : filtered.length === 0 ? (
        <p className="text-slate-400 text-sm text-center mt-10">No services found</p>
      ) : (
        <div className="space-y-3">
          {filtered.map(s => {
            const ui = STATUS_UI[s.status] || { label: s.status || "Unknown", bg: "#f3f4f6", color: "#374151", border: "#d1d5db" };
            const isPendingApproval = s.status === SERVICE_STATUS.COMPLETED_PENDING_APPROVAL;
            const isInProgress      = s.status === SERVICE_STATUS.IN_PROGRESS;
            const isPartial         = s.status === SERVICE_STATUS.PARTIAL;
            const isClosed          = s.status === SERVICE_STATUS.CLOSED || s.status === "completed";
            const duration          = getWorkingDuration(s.started_at);
            const lastRemark        = s.remarks_log?.length > 0
              ? s.remarks_log[s.remarks_log.length - 1]
              : null;

            return (
              <div key={s.id} onClick={() => navigate(`/services/${s.id}`)}
                className="bg-white rounded-2xl shadow-sm border border-slate-100 cursor-pointer hover:shadow-md transition overflow-hidden"
                style={{ borderLeft: `4px solid ${ui.border}` }}>

                {/* ── TOP ROW ── */}
                <div className="p-4 pb-2">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-semibold text-slate-800">{s.complaint || "No complaint"}</p>
                        {isPendingApproval && (
                          <span className="text-[10px] bg-violet-100 text-violet-700 font-bold px-2 py-0.5 rounded-full animate-pulse">
                            ⚡ ACTION NEEDED
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-slate-500 mt-0.5">
                        {s.client_name} → {s.site_name}
                      </p>
                    </div>
                    <span className="text-[11px] font-bold px-3 py-1 rounded-full ml-2 shrink-0"
                      style={{ background: ui.bg, color: ui.color }}>
                      {ui.label}
                    </span>
                  </div>
                </div>

                {/* ── DETAILS GRID ── */}
                <div className="px-4 pb-3 grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-2 mt-1">

                  {/* Scheduled date */}
                  {s.scheduled_date && (
                    <div>
                      <p className="text-[10px] text-slate-400 uppercase font-semibold">Scheduled</p>
                      <p className="text-xs text-slate-600 font-medium">📅 {s.scheduled_date}</p>
                    </div>
                  )}

                  {/* Assigned techs */}
                  {s.assigned_techs?.length > 0 && (
                    <div>
                      <p className="text-[10px] text-slate-400 uppercase font-semibold">Technician</p>
                      <p className="text-xs text-slate-600 font-medium">
                        👷 {s.assigned_techs.map(t =>
                          `${t.name}${t.accepted === true ? " ✓" : t.accepted === false ? " ✕" : ""}`
                        ).join(", ")}
                      </p>
                    </div>
                  )}

                  {/* Old doc assigned_to */}
                  {!s.assigned_techs?.length && s.assigned_to_name && (
                    <div>
                      <p className="text-[10px] text-slate-400 uppercase font-semibold">Technician</p>
                      <p className="text-xs text-slate-600 font-medium">👷 {s.assigned_to_name}</p>
                    </div>
                  )}

                  {/* Working duration — only for active jobs */}
                  {(isInProgress || isPartial) && duration && (
                    <div>
                      <p className="text-[10px] text-slate-400 uppercase font-semibold">Working Time</p>
                      <p className="text-xs font-semibold" style={{ color: "#D97706" }}>
                        ⏱ {duration}
                      </p>
                    </div>
                  )}

                  {/* Photos count */}
                  {s.photos?.length > 0 && (
                    <div>
                      <p className="text-[10px] text-slate-400 uppercase font-semibold">Photos</p>
                      <p className="text-xs text-slate-600 font-medium">📷 {s.photos.length} uploaded</p>
                    </div>
                  )}

                  {/* Defective items */}
                  {s.defective_items?.length > 0 && (
                    <div>
                      <p className="text-[10px] text-slate-400 uppercase font-semibold">Defective Items</p>
                      <p className="text-xs text-red-500 font-medium">⚠ {s.defective_items.length} item(s)</p>
                    </div>
                  )}

                  {/* Job type — for closed jobs */}
                  {isClosed && s.job_type && (
                    <div>
                      <p className="text-[10px] text-slate-400 uppercase font-semibold">Job Type</p>
                      <p className="text-xs text-slate-600 font-medium">
                        🏷 {s.job_type === "other"
                          ? (s.job_type_other || "Other")
                          : JOB_TYPE_LABELS[s.job_type] || s.job_type}
                      </p>
                    </div>
                  )}

                  {/* Chargeable — for closed jobs */}
                  {isClosed && s.is_chargeable && (
                    <div>
                      <p className="text-[10px] text-slate-400 uppercase font-semibold">Billing</p>
                      <p className="text-xs font-semibold text-green-600">
                        💰 ₹{Number(s.chargeable_amount || 0).toLocaleString()}
                      </p>
                    </div>
                  )}

                  {/* Closed by */}
                  {isClosed && s.closed_by_name && (
                    <div>
                      <p className="text-[10px] text-slate-400 uppercase font-semibold">Closed By</p>
                      <p className="text-xs text-slate-600 font-medium">✓ {s.closed_by_name}</p>
                    </div>
                  )}

                  {/* Completion type — pending/closed */}
                  {(isPendingApproval || isClosed) && s.completion_type && (
                    <div>
                      <p className="text-[10px] text-slate-400 uppercase font-semibold">Completion</p>
                      <p className="text-xs text-slate-600 font-medium capitalize">
                        {s.completion_type === "complete" ? "✅ Complete" : "⚡ Partial"}
                      </p>
                    </div>
                  )}
                </div>

                {/* ── SERVICE REPORT (pending/closed) ── */}
                {(isPendingApproval || isClosed) && s.service_report && (
                  <div className="mx-4 mb-3 p-2 bg-slate-50 rounded-xl border border-slate-100">
                    {s.issue_found && (
                      <p className="text-[10px] text-slate-400 uppercase font-semibold mb-0.5">Issue Found</p>
                    )}
                    {s.issue_found && (
                      <p className="text-xs text-slate-600 mb-1">{s.issue_found}</p>
                    )}
                    {s.resolution && (
                      <p className="text-[10px] text-slate-400 uppercase font-semibold mb-0.5">Work Done</p>
                    )}
                    {s.resolution && (
                      <p className="text-xs text-slate-600 mb-1">{s.resolution}</p>
                    )}
                    <p className="text-[10px] text-slate-400 uppercase font-semibold mb-0.5">Summary</p>
                    <p className="text-xs text-slate-600">{s.service_report}</p>
                  </div>
                )}

                {/* ── LAST REMARK (active jobs only) ── */}
                {(isInProgress || isPartial || s.status === SERVICE_STATUS.ASSIGNED) && lastRemark && (
                  <div className="mx-4 mb-3 p-2 bg-amber-50 rounded-xl border border-amber-100">
                    <p className="text-[10px] text-amber-600 uppercase font-semibold mb-0.5">
                      💬 Latest Remark — {lastRemark.by_name}
                    </p>
                    <p className="text-xs text-slate-700">{lastRemark.text}</p>
                  </div>
                )}

              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
